/**
 * The drawn "photos" behind the scenes: a bright living room (wall, framed
 * print, sofa, doorway to a lit kitchen, plant, wood floor) and an aerial
 * view of a block of land split into lots. Everything is in card units (256x144).
 */

const stop = (o: number, c: string, a?: number) => <stop offset={o} stopColor={c} stopOpacity={a} />;

/** Thumbnails of the link picker: a room in four palettes. */
export const THUMBS = [
  ['ta-t1', '#f6f1e9', '#e3d8c9', '#b98f66', '#a27852'],
  ['ta-t2', '#eef1f3', '#d9dde2', '#c4b39d', '#ad9a82'],
  ['ta-t3', '#f3f5f4', '#d8dedd', '#cfd4d3', '#b7bebd'],
  ['ta-t4', '#bcdcf2', '#e7eef1', '#b8b2a8', '#9c958a'],
] as const;

export function Defs() {
  return (
    <defs>
      <linearGradient id="ta-wall" x1="0" y1="0" x2="1" y2="1">
        {stop(0, '#f5f1ea')}
        {stop(1, '#e6dfd4')}
      </linearGradient>
      <linearGradient id="ta-floor" x1="0" y1="0" x2="0" y2="1">
        {stop(0, '#c9a47e')}
        {stop(1, '#a87e57')}
      </linearGradient>
      <linearGradient id="ta-kitchen" x1="0" y1="0" x2="0" y2="1">
        {stop(0, '#fffaf2')}
        {stop(1, '#efe5d6')}
      </linearGradient>
      <radialGradient id="ta-sun" cx="0" cy="0" r="1">
        {stop(0, '#fff7e6', 0.7)}
        {stop(1, '#fff7e6', 0)}
      </radialGradient>
      {THUMBS.map(([id, a, b, c, d]) => (
        <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
          {stop(0, a)}
          {stop(0.55, b)}
          {stop(0.56, c)}
          {stop(1, d)}
        </linearGradient>
      ))}
      {/* top-left sheen of the editor's black glass (`--editor-sheen`, 170deg) */}
      <linearGradient id="ta-sheen" x1="0.08" y1="0" x2="0" y2="1">
        {stop(0, '#ffffff', 0.07)}
        {stop(0.4, '#ffffff', 0)}
      </linearGradient>
      <filter id="ta-lbl" x="-30%" y="-60%" width="160%" height="220%">
        <feDropShadow dx="0" dy="1" stdDeviation="1.2" floodColor="#000" floodOpacity=".75" />
      </filter>
      <filter id="ta-pop" x="-10%" y="-10%" width="120%" height="130%" colorInterpolationFilters="sRGB">
        <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity=".35" />
      </filter>
      <clipPath id="ta-door">
        <rect x="150" y="20" width="58" height="84" />
      </clipPath>
    </defs>
  );
}

const BOARDS: readonly (readonly [number, number])[] = [
  [20, -40],
  [58, -24],
  [96, -8],
  [134, 8],
  [172, 24],
  [210, 40],
  [248, 52],
];

/**
 * The living room. `dy` moves the camera down (the room goes up) to look at
 * the floor; `ox` shifts it sideways (a second copy to pan into).
 */
export function Room({ dy = 0, ox = 0, rug }: { dy?: number; ox?: number; rug?: boolean }) {
  return (
    <g transform={`translate(${ox} ${-dy})`}>
      <rect x="0" y="0" width="256" height="150" fill="url(#ta-wall)" />
      <rect x="0" y="0" width="256" height="150" fill="url(#ta-sun)" />
      <rect x="0" y="104" width="256" height="130" fill="url(#ta-floor)" />
      <g stroke="#7a5638" strokeOpacity=".16">
        <path d={BOARDS.map(([x, k]) => `M${x} 104 L${x + k * 2} 184`).join(' ')} />
      </g>
      <rect x="0" y="101" width="256" height="4" fill="#f8f5f0" />
      {/* framed print */}
      <rect x="18" y="16" width="64" height="40" fill="#d9e3df" stroke="#3a3632" strokeWidth="2" />
      <path d="M22 52 Q40 30 56 42 T78 36 V52 Z" fill="#a9bdb4" />
      {/* doorway to the kitchen */}
      <g clipPath="url(#ta-door)">
        <rect x="150" y="20" width="58" height="84" fill="url(#ta-kitchen)" />
        <rect x="154" y="30" width="50" height="18" fill="#e7e2da" stroke="#d6cfc4" />
        <rect x="150" y="76" width="58" height="28" fill="#d9d3ca" />
        <rect x="150" y="72" width="58" height="5" fill="#bfb8ad" />
        <circle cx="190" cy="66" r="4" fill="#9fb59a" />
        <rect x="187" y="66" width="6" height="6" fill="#efe9e0" />
      </g>
      <rect x="150" y="20" width="58" height="84" fill="none" stroke="#ffffff" strokeWidth="3" />
      {/* sofa */}
      <rect x="-8" y="72" width="122" height="34" rx="6" fill="#b9b1a6" />
      <rect x="-4" y="60" width="56" height="22" rx="6" fill="#c8c1b7" />
      <rect x="54" y="60" width="54" height="22" rx="6" fill="#c8c1b7" />
      <rect x="104" y="70" width="14" height="36" rx="6" fill="#aea69a" />
      <rect x="22" y="70" width="18" height="13" rx="4" fill="#d9c7a7" />
      {/* side table and plant */}
      <rect x="224" y="86" width="22" height="20" rx="3" fill="#e2d9cc" />
      <circle cx="235" cy="74" r="12" fill="#6c8c5c" />
      <circle cx="226" cy="66" r="8" fill="#7d9e6a" />
      <circle cx="245" cy="67" r="9" fill="#5f7f50" />
      {rug && (
        <g>
          <path d={RUG_OUT} fill="#6f8f8a" />
          <path d={RUG_IN} fill="#8fb0aa" />
        </g>
      )}
    </g>
  );
}

/** Rug on the floor (room coordinates, before `dy`). */
export const RUG_OUT = 'M60 112 L200 112 L218 142 L42 142 Z';
export const RUG_IN = 'M66 116 L194 116 L208 138 L52 138 Z';
/** x of the rug's outline at fraction `f` of its width, top and bottom edge. */
export const rugX = (f: number): readonly [number, number] => [60 + 140 * f, 42 + 176 * f];

/** Lot grid of the aerial block: 3 columns x 2 rows inside x 80..176, y 24..120. */
export const BLOCK = { x0: 80, x1: 176, y0: 24, y1: 120, cols: [112, 144], row: 72 } as const;
const GRASS = ['#8fb86a', '#a3c47a', '#86ad66', '#9bbf72', '#7fa862', '#b0c985'];

/** Drone view, straight down: a block of land split into lots by dirt paths and fences, with its street. */
export function AerialLand() {
  const { x0, x1, y0, y1, cols, row } = BLOCK;
  const xs = [x0, ...cols, x1];
  const ys = [y0, row, y1];
  const paths = `${cols.map(x => `M${x} ${y0} V${y1}`).join(' ')} M${x0} ${row} H${x1}`;
  const tree = (cx: number, cy: number, r: number) => (
    <g key={`${cx}-${cy}`}>
      <circle cx={cx + 1.5} cy={cy + 2} r={r} fill="#000" opacity=".18" />
      <circle cx={cx} cy={cy} r={r} fill="#4f7d43" />
      <circle cx={cx - r * 0.3} cy={cy - r * 0.3} r={r * 0.55} fill="#6a9a58" />
    </g>
  );
  return (
    <g>
      {/* surroundings: fields */}
      <rect width="256" height="144" fill="#9cbc78" />
      {tree(18, 30, 7)}
      {tree(34, 98, 6)}
      {tree(232, 36, 7)}
      {tree(222, 108, 6)}
      <rect x="0" y="52" width="40" height="34" fill="#b7c987" />
      <rect x="216" y="56" width="40" height="30" fill="#c2cf8e" />
      {/* street around the block */}
      <rect x="52" y="0" width="22" height="144" fill="#6f747b" />
      <rect x="182" y="0" width="22" height="144" fill="#6f747b" />
      <rect x="52" y="0" width="152" height="18" fill="#6f747b" />
      <rect x="52" y="126" width="152" height="18" fill="#6f747b" />
      <path d="M63 0 V144 M193 0 V144 M52 9 H204 M52 135 H204" stroke="#f1ecd6" strokeOpacity=".8" strokeWidth="1.2" strokeDasharray="5 4" />
      {/* curb and sidewalk */}
      <rect x="74" y="18" width="108" height="108" fill="#d4cfc2" />
      {/* lots */}
      {ys.slice(0, -1).flatMap((ya, r) =>
        xs.slice(0, -1).map((xa, c) => (
          <rect key={`${r}${c}`} x={xa} y={ya} width={(xs[c + 1] ?? xa) - xa} height={(ys[r + 1] ?? ya) - ya} fill={GRASS[r * 3 + c]} />
        )),
      )}
      {/* dirt paths and fences between lots: the contrast Lotear finds */}
      <path d={paths} stroke="#e1d3a8" strokeWidth="3.2" />
      <path d={paths} stroke="#6b5a38" strokeWidth=".7" strokeDasharray="1.6 1.6" />
    </g>
  );
}
